// BUG-081: Use named import from @sentry/react (re-exported from @sentry/browser).
// Previously imported from @sentry/tracing, a deprecated re-export shim that caused
// ReferenceError: TrackerStorageType is not defined at runtime due to webpack
// tree-shaking breaking internal references. Named import lets the bundler verify
// the export at build time, avoiding runtime destructure surprises.
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/react";
import { DEPLOY_CHANNEL, ENV_DEVELOPMENT, ENV_PRODUCTION } from "../config/appConstants";

// IMP-432 (#380) — 환경 판별을 호스트에서 분리한다.
// 종전에는 ddb.js `getEnv()`(raymedical.com 도메인만 production) 를 공유했으나,
// ① RAYGuard 인프라는 단일(dev 별도 인프라 부재)이라 호스트로 갈라야 할 실체가 없고,
// ② DDB 라우팅과 Sentry 가 같은 판정을 공유해 한쪽 정책 변경이 다른 쪽으로 새어 나갔다
//    (로컬에서 DDB 를 prod 로 보려고 getEnv 를 고치면 localhost 에러가 프로덕션 Sentry 로 유입 —
//     실제 prod Sentry 에 localhost:3000 이벤트 6건 유입 확인, 2026-07-31).
//
// 대신 두 축을 **분리**한다 (PR #545 review R2):
//   ① 배포 채널(environment) = `DEPLOY_CHANNEL` — 앱 전역 개념이므로 관측 모듈이 아니라
//      `config/appConstants.js` 에 산다(review R3). 배선 출처·미배선 현황·화이트리스트 정책은
//      그 파일의 "환경 신호 3계열" 블록이 SSoT 다. 여기서는 소비만 한다.
//   ② 송출 여부(enabled) = 빌드 모드 `NODE_ENV` — "로컬 dev 서버인가" 만 판정한다.
//      `craco build` 는 어느 배포 경로에서도 NODE_ENV=production 을 강제하므로 이 값으로는
//      배포 채널을 구분할 수 없다. 로컬 `yarn s`(NODE_ENV=development) 의 에러만 미송출.
const isLocalDevServerBuild = process.env.NODE_ENV === ENV_DEVELOPMENT;

// ─── URL 스크러빙 (PR #545 review R3~R5) ─────────────────────────────────────
// 계기: `pages/auth/ResetNewPassword.js:25-34` 는 비밀번호 재설정 키를 `?key=<10자>` 로 받는다.
//
// 민감 쿼리가 이벤트에 실리는 경로는 아래 목록이다. 축이 라운드마다 하나씩 발견돼(R3 ①②③ →
// R4 ④ → R5 ⑤ → R7 ⑥) 개수 표기가 세 번 드리프트했으므로 **개수는 적지 않는다**(review R8 — F3).
// 축이 늘면 이 목록과 `sentry.test.js` 헤더를 함께 갱신하고, 스크럽 자체는 `scrubRequest` /
// `scrubSpan` / `scrubTags` 에만 둔다 — 진입점이 아니라 그쪽이 SSoT 다.
//
// 새 축을 추가할 때 **반드시 손으로 확인할 것: SDK 가 그 값을 두 벌 들고 있지 않은가.**
// 이 PR 에서 같은 실패가 **세 번** 났다 — `span.data` vs `_attributes`(R6 D1),
// `event.tags` vs `contexts.trace.tags`(R8 F1), console `data.arguments` vs `message`(R10 S1).
// 셋 다 손으로 만든 픽스처로는 잡히지 않았다.
// `sentry.test.js` 의 전량 스캔 가드는 **SDK 조립 경로로 만든 transaction 이벤트**를 훑지만,
// 그 이벤트에 값이 실리는 축만 본다 — 자동 탐지기가 아니므로 위 수동 확인을 대체하지 않는다(R9 G1).
//
// 축 목록:
//   ① `event.request.url` — HttpContext 기본 통합이 `location.href` 를 그대로 싣는다
//   ② navigation breadcrumb 의 `data.from` / `data.to` — query string 포함 전문
//   ③ console breadcrumb 의 `data.arguments` — 값 자체를 제거
//   ④ `event.request.headers.Referer` — `document.referrer`. 하드 내비게이션 이후 **그 document
//      수명 동안 모든 이벤트**에 실려 ① 보다 노출 창이 넓다(SPA 이동으로 갱신되지 않음)
//   ⑤ **transaction 이벤트** — `@sentry/core` 는 `type === 'transaction'` 을 `beforeSend` 가 아니라
//      `beforeSendTransaction` 으로 보낸다. 반면 HttpContext 는 `preprocessEvent` 훅이라 모든 타입에
//      실행되므로 `request.url`·`Referer` 가 채워진다. BrowserTracing 의 pageload transaction 은
//      **화면 진입만으로** 샘플링 전송되어 error 축보다 노출 창이 넓다. span 의 `description` 과
//      URL attributes(`http.url`·`url`)도 전문이라 함께 스크럽한다 — 아래 `scrubSpan` 참조.
//   ⑥ `event.tags['lcp.url']` **및 `event.contexts.trace.tags`** — LCP 이미지가 signedUrl 이면
//      BrowserTracing 이 `setTag('lcp.url', …)` 한다. 두 벌 실리므로 양쪽 모두 스크럽한다(R7/R8)
//   그 외 breadcrumb(xhr/fetch)의 `data.url` — 카테고리 화이트리스트가 아니라 "`data.url` 이 있으면
//      스크럽" 으로 뒤집어 새 crumb 타입이 추가돼도 기본이 닫히게 한다(review R5).
// 앞 6키는 **앱이 스스로 만드는 쿼리**, 뒤 5키는 **리소스 URL 로 유입되는 presigned 서명**이다
// (review R7 — E1). `FileUpload.js` 의 signedUrl 이 `<img src>` 로 로드되면 BrowserTracing 이
// resource span 을 만들고 `description = resourceUrl`(쿼리 포함 전문)을 넣는다.
// 정규식이 `[?&]<key>=` 키 경계를 요구하므로 `token` 이 `X-Amz-Security-Token=` 을 커버하지 못한다
// — 별도 항목이어야 동작한다(`autologin_code` 와 같은 이유). 플래그가 `gi` 라 대소문자·`-` 는 그대로.
const SENSITIVE_QUERY_KEYS = [
  'key', 'token', 'code', 'secret', 'password', 'autologin_code',
  'x-amz-signature', 'x-amz-credential', 'x-amz-security-token', 'signature', 'awsaccesskeyid',
];

// `?` 부재 early-return 은 동치 no-op(방어용)이다 — 정규식이 `[?&]` 키 경계를 요구하므로
// fragment-only URL 은 애초에 매칭되지 않는다. `typeof` 가드는 load-bearing 이다(navigation
// crumb 의 `from`/`to` 중 한쪽만 존재하는 경우 등).
const scrubUrl = (url) => {
  if (typeof url !== 'string' || url.indexOf('?') === -1) return url;
  return SENSITIVE_QUERY_KEYS.reduce(
    (acc, k) => acc.replace(new RegExp('([?&]' + k + '=)[^&#]*', 'gi'), '$1[redacted]'),
    url,
  );
};

// ─── span URL 스크럽 (PR #545 review R6 — D1) ────────────────────────────────
// ⚠️ SDK 내부 직렬화 스키마에 의존하는 지점이다. `@sentry/core` v7 의 `Span` 은
//   `getSpanJSON()` → `_getData()` 에서 `{ ...this.data, ...this._attributes }` 로
//   **attributes 를 뒤에 병합**한다(`tracing/span.js:618-640`). 그리고 xhr/fetch 계측은 URL 을
//   `data` 가 아니라 **attributes** 에 넣으며 키가 두 개다 — `http.url` 과 형제 `url`
//   (`@sentry-internal/tracing` `browser/request.js:233-241`, `common/fetch.js:45-58`).
//   따라서 `span.data` 만 고치면 직렬화에서 원본으로 되돌아가 **프로덕션 무효**가 된다(R6 지적).
//   `attributes` getter 는 `_attributes` 를 그대로 반환하므로 그 객체를 손보면 반영된다.
//   `description` 은 setter 가 `_name` 을 바꾸므로 대입으로 동작한다.
//   테스트는 POJO 대신 **SDK 실물 Span** 으로 `spanToJSON()` 결과를 단언해야 이 발산을 잡는다.
const SPAN_URL_KEYS = ['http.url', 'url'];

const scrubSpan = (span) => {
  if (!span) return;
  if (typeof span.description === 'string') {
    span.description = scrubUrl(span.description);
  }
  [span.data, span.attributes].forEach((bag) => {
    if (!bag) return;
    SPAN_URL_KEYS.forEach((k) => {
      if (typeof bag[k] === 'string') bag[k] = scrubUrl(bag[k]);
    });
  });
};

// URL 을 담는 태그 키. 값이 두 벌 복제되는 구조라 호출부에서 두 지점 모두에 적용한다.
const TAG_URL_KEYS = ['lcp.url'];

const scrubTags = (bag) => {
  if (!bag) return;
  TAG_URL_KEYS.forEach((k) => {
    if (typeof bag[k] === 'string') bag[k] = scrubUrl(bag[k]);
  });
};

// event.request(url + Referer) + span URL + tags 스크럽. `beforeSend` / `beforeSendTransaction` 양쪽에서
// 같은 함수를 호출한다 — 콜백이 갈라지면서 한쪽만 닫히는 것이 R5 의 결함이었다.
// Referer 헤더 키는 SDK 가 대문자로 넣지만, 소문자로 실리는 경로가 생겨도 새지 않게 대소문자 무시로 찾는다.
const scrubRequest = (event) => {
  if (!event) return event;

  if (event.request) {
    if (typeof event.request.url === 'string') {
      event.request.url = scrubUrl(event.request.url);
    }
    const headers = event.request.headers;
    if (headers) {
      const refererKeys = Object.keys(headers).filter((k) => /^referer$/i.test(k));
      if (refererKeys.length > 0) {
        const next = { ...headers };
        refererKeys.forEach((k) => {
          if (typeof next[k] === 'string') next[k] = scrubUrl(next[k]);
        });
        event.request.headers = next;
      }
    }
  }

  if (Array.isArray(event.spans)) {
    event.spans.forEach(scrubSpan);
  }

  // URL 축은 `request`/`spans` 밖에도 있다 — LCP 이미지가 signedUrl 이면 BrowserTracing 이
  // `transaction.setTag('lcp.url', …)` 로 tags 에 URL 을 싣는다(`metrics/index.js:609-613`).
  //
  // ⚠️ 태그는 이벤트에 **두 벌** 실린다(review R8 — F1). `Transaction._finishTransaction()` 이
  //   `tags: this.tags`(원본 참조)와 `contexts.trace = spanToTraceContext(this)` 를 함께 넣는데,
  //   후자는 `dropUndefinedKeys` 가 POJO 를 **재귀 복제**한 딥 카피다
  //   (`tracing/transaction.js:303-313` → `utils/spanUtils.js:14-18` → `utils/object.js:205-224`).
  //   따라서 `event.tags` 만 고치면 `contexts.trace.tags` 에 원문이 그대로 전송된다 — 실물 SDK 로
  //   재현 확인. R6 D1(`span.data` vs `_attributes`)과 **같은 계열**이다:
  //   "SDK 가 같은 값을 두 벌 들고 있고 POJO 픽스처로는 발산이 안 잡힌다".
  scrubTags(event.tags);
  scrubTags(event.contexts && event.contexts.trace && event.contexts.trace.tags);

  return event;
};

// Transient AxiosError messages to drop (not actionable).
// Match types are explicit to avoid unintended substring collisions:
//   - exact:    msg === value
//   - includes: msg.includes(value)
// Related bugs:
//   - BUG-013 (Network Error) — exact Axios message
//   - BUG-061       ('timeout exceeded') — legacy Axios suffix
//   - BUG-076/080   ('timeout of {ms}ms exceeded') — dynamic, needs prefix match
const TRANSIENT_PATTERNS = [
  { type: 'exact', value: 'Network Error' },
  { type: 'includes', value: 'timeout exceeded' },
  { type: 'includes', value: 'timeout of ' },
  // Sentry #5/#6(Request aborted): Axios CancelToken 또는 요청 중단 — 비액션성
  { type: 'exact', value: 'Request aborted' },
];

const matchesTransient = (msg) =>
  typeof msg === 'string' &&
  TRANSIENT_PATTERNS.some((p) =>
    p.type === 'exact' ? msg === p.value : msg.includes(p.value)
  );

const isTransientAxios = (err) =>
  err &&
  (err.name === 'AxiosError' || err.isAxiosError) &&
  !err.response &&
  matchesTransient(err.message);

Sentry.init({
  dsn: "https://0e37913fd3aec9c2463e214114d3dfb8@o4504930404925440.ingest.us.sentry.io/4510985053339648",
  integrations: [new BrowserTracing()],
  environment: DEPLOY_CHANNEL,
  // dev 배포 채널은 트랜잭션 쿼터를 잠식하지 않도록 낮게 유지.
  tracesSampleRate: DEPLOY_CHANNEL === ENV_PRODUCTION ? 0.2 : 0.05,
  enabled: !isLocalDevServerBuild,
  // breadcrumb 리댁션 (PR #545 review R2/R3, CWE-200).
  //   console   — 인자가 **두 벌** 실린다(review R10 — S1. 이 PR 세 번째 "두 벌" 사례):
  //               ⓐ `data.arguments` — 인자 객체 그대로. `lib/ddb.js` 에러 로그가 게이트웨이 응답
  //                  본문(라이선스·고객 레코드)과 `sk` 를 넘기므로 제거한다.
  //               ⓑ `message` — breadcrumbs 통합이 `safeJoin(args, ' ')` 로 전 인자를 join 한다
  //                  (`integrations/breadcrumbs.js:159-167`, `safeJoin` = `String(value)`).
  //                  객체는 `[object Object]` 가 되지만 **문자열 인자는 원문이 그대로 남는다** —
  //                  URL 을 문자열로 넘기는 로그가 하나만 생겨도 ⓐ 리댁션을 우회한다.
  //                  그래서 message 도 같은 정책으로 스크럽한다(진단 문구는 유지, URL 값만 가림).
  //   navigation — `data.from` / `data.to` 가 query string 포함 전문이라 재설정 키가 실린다.
  //   그 외      — `data.url`(xhr/fetch) 이 있으면 카테고리와 무관하게 스크럽한다. 화이트리스트
  //                방식은 새 crumb 타입이 추가될 때 열린 채로 남으므로 기본을 닫는 쪽으로 뒤집었다(R5).
  beforeBreadcrumb(breadcrumb) {
    if (!breadcrumb) return breadcrumb;

    // `message` 는 `data` 유무와 무관하게 실릴 수 있으므로 data 가드보다 앞에서 처리한다.
    const scrubbedMessage = typeof breadcrumb.message === 'string'
      ? scrubUrl(breadcrumb.message)
      : breadcrumb.message;
    const messageChanged = scrubbedMessage !== breadcrumb.message;

    if (!breadcrumb.data) {
      return messageChanged ? { ...breadcrumb, message: scrubbedMessage } : breadcrumb;
    }

    // 카테고리 무관 처리를 **먼저** 둔다. 종전에는 console limb 이 먼저 return 해서
    // "`data.url` 이 있으면 스크럽" 규약에 주석 없는 예외가 하나 남아 있었다(review R6 — D5).
    const data = { ...breadcrumb.data };
    let changed = messageChanged;

    if (typeof data.url === 'string') {
      data.url = scrubUrl(data.url);
      changed = true;
    }
    if (breadcrumb.category === 'navigation') {
      data.from = scrubUrl(data.from);
      data.to = scrubUrl(data.to);
      changed = true;
    }
    if (breadcrumb.category === 'console' && 'arguments' in data) {
      delete data.arguments;
      changed = true;
    }

    return changed ? { ...breadcrumb, message: scrubbedMessage, data } : breadcrumb;
  },
  // transaction 이벤트는 `beforeSend` 를 타지 않는다(`@sentry/core` 가 타입으로 콜백을 가른다).
  // BrowserTracing 의 pageload transaction 은 화면 진입만으로 전송되므로 error 축보다 노출 창이 넓다.
  beforeSendTransaction(event) {
    return scrubRequest(event);
  },
  beforeSend(event, hint) {
    // HttpContext 통합이 채우는 `request`(url + Referer)와 span URL 스크러빙.
    // drop 필터보다 먼저 적용해 어느 경로로 반환되든 누락이 없게 한다.
    // `public/index.html` 에 `<meta name="referrer">` 가 없어 브라우저 기본
    // strict-origin-when-cross-origin 이 적용되고, same-origin 이동은 쿼리 포함 전문이 남는다.
    scrubRequest(event);

    const error = hint && hint.originalException;

    // Direct AxiosError check (standard path)
    if (isTransientAxios(error)) {
      return null;
    }

    // PromiseRejectionEvent wrapper check (some browsers wrap unhandled rejections)
    const reason = error && error.reason;
    if (isTransientAxios(reason)) {
      return null;
    }

    // Fallback: check Sentry event exception values for transient AxiosError
    const exceptions = event.exception && event.exception.values;
    if (exceptions && exceptions.some(
      (ex) => ex.type === 'AxiosError' && matchesTransient(ex.value)
    )) {
      return null;
    }

    // --- Native fetch() TypeError filter (BUG-064) ---
    // Browser native fetch() throws TypeError on network failures (CORS, connectivity loss, DNS).
    // Messages vary by browser:
    //   Chrome/Edge  : "Failed to fetch"
    //   Safari       : "Load failed"                                    (Sentry #7)
    //   Firefox      : "NetworkError when attempting to fetch resource." (Sentry #8)
    // Not actionable on server side.
    const FETCH_FAIL_MESSAGES = new Set([
      'Failed to fetch',
      'Load failed',
      'NetworkError when attempting to fetch resource.',
    ]);

    // Direct TypeError check
    if (
      error instanceof TypeError &&
      FETCH_FAIL_MESSAGES.has(error.message)
    ) {
      return null;
    }

    // PromiseRejectionEvent wrapper check for TypeError
    if (
      reason instanceof TypeError &&
      FETCH_FAIL_MESSAGES.has(reason.message)
    ) {
      return null;
    }

    // Fallback: check Sentry event exception values for TypeError fetch failures
    if (exceptions && exceptions.some(
      (ex) => ex.type === 'TypeError' && FETCH_FAIL_MESSAGES.has(ex.value)
    )) {
      return null;
    }

    // --- Media play() AbortError filter (BUG-092) ---
    // Browser throws AbortError when play() is called on a media element that is
    // removed from the DOM before the play Promise resolves (e.g., Splide carousel
    // autoplay or embedded video during page navigation). Not actionable.
    // Ref: https://goo.gl/LdLk22
    const PLAY_ABORT_RE = /The play\(\) request was interrupted/;

    if (
      error instanceof DOMException &&
      error.name === 'AbortError' &&
      PLAY_ABORT_RE.test(error.message)
    ) {
      return null;
    }

    if (
      reason instanceof DOMException &&
      reason.name === 'AbortError' &&
      PLAY_ABORT_RE.test(reason.message)
    ) {
      return null;
    }

    if (exceptions && exceptions.some(
      (ex) => (ex.type === 'AbortError' || ex.type === 'Error') && PLAY_ABORT_RE.test(ex.value)
    )) {
      return null;
    }

    // --- react-qr-reader WebRTC TypeError filter (BUG-065) ---
    // react-qr-reader v2 internally checks 'ontrack' in RTCPeerConnection.
    // On browsers without full WebRTC support the target object is undefined,
    // producing: TypeError: Cannot use 'in' operator to search for 'ontrack' in undefined.
    // This is a third-party library issue on unsupported browsers — not actionable.
    const IN_OPERATOR_RE = /Cannot use 'in' operator to search for '.*' in undefined/;

    if (
      error instanceof TypeError &&
      IN_OPERATOR_RE.test(error.message)
    ) {
      return null;
    }

    if (
      reason instanceof TypeError &&
      IN_OPERATOR_RE.test(reason.message)
    ) {
      return null;
    }

    if (exceptions && exceptions.some(
      (ex) => ex.type === 'TypeError' && IN_OPERATOR_RE.test(ex.value)
    )) {
      return null;
    }

    // --- extDomain ReferenceError filter (Sentry #9) ---
    // Browser extensions inject extDomain into the page scope. When the script
    // executes before the extension fully initialises, a ReferenceError is thrown
    // inside third-party extension code. Not actionable on our side.

    // Direct ReferenceError check
    if (
      error instanceof ReferenceError &&
      error.message === 'extDomain is not defined'
    ) {
      return null;
    }

    // PromiseRejectionEvent wrapper check for ReferenceError
    if (
      reason instanceof ReferenceError &&
      reason.message === 'extDomain is not defined'
    ) {
      return null;
    }

    // Fallback: check Sentry event exception values
    if (exceptions && exceptions.some(
      (ex) => ex.type === 'ReferenceError' && ex.value === 'extDomain is not defined'
    )) {
      return null;
    }

    // --- ionicons style 크래시 filter (Sentry #Z) ---
    // ionicons web component teardown 시 shadow DOM style 접근 시도.
    // 앱 코드(src/)가 아닌 ionicons 번들 프레임이 origin 인 경우만 drop.
    // 과잉 억제 방지: ionicons 프레임 없이 동일 메시지가 앱 코드에서 발생하면 drop 하지 않음.
    const isIoniconsFrame = (f) => {
      const p = (f && (f.filename || f.abs_path || f.absPath)) || '';
      return typeof p === 'string' && p.includes('ionicons');
    };
    const IONICONS_STYLE_RE = /(reading 'style'|evaluating 's\.style')/;

    if (exceptions && exceptions.some(
      (ex) =>
        ex.type === 'TypeError' &&
        IONICONS_STYLE_RE.test(ex.value || '') &&
        Array.isArray(ex.stacktrace && ex.stacktrace.frames) &&
        ex.stacktrace.frames.some(isIoniconsFrame)
    )) {
      return null;
    }

    return event;
  },
});
